import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import {
  adminUserErrorsSelector,
  createAdminUser,
  getAdminEditUser,
  isLoadingUserSelector,
  selectedUserSelector,
  setAdminUserErrors,
  setSelectedUser,
  updateAdminUser,
} from 'src/store/adminUsers';

import {
  CustomButton,
  CustomInput,
  CustomPagination,
  // CustomSelect,
  CustomSwitch,
  PageLoader,
  WhiteCard,
} from 'src/components';

import './userForm.scss';
import moment from 'moment';
import { ProfileInfoIcon } from 'src/assets';
import { REACT_APP_API_URL } from 'src/constants';
import classNames from 'classnames';

export const UserForm = (props) => {
  const { t } = useTranslation('userForm');

  // const options = [
  //   {
  //     label: t('value1'),
  //     value: 'value1',
  //   },
  //   {
  //     label: t('value2'),
  //     value: 'value2',
  //   },
  //   {
  //     label: t('numbers'),
  //     value: 'numbers',
  //   },
  // ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const selectedUser = useSelector(selectedUserSelector);
  const isLoading = useSelector(isLoadingUserSelector);
  const formError = useSelector(adminUserErrorsSelector);

  const isEditMode = pathname?.includes('edit');

  const [formData, setFormData] = useState({
    login: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [selectedTab, setSelectedTab] = useState('1');

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditUser(params?.id));
    }

    return () => {
      dispatch(setSelectedUser({}));
      dispatch(setAdminUserErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedUser }));
    }
  }, [isEditMode, selectedUser]);

  const onClickTab = useCallback(
    (tab) => () => {
      setSelectedTab(tab);
    },
    [],
  );

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminUserErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdminUser({ id: params?.id, payload: formData, navigate }));
        return;
      }

      await dispatch(createAdminUser({ navigate, payload: formData }));
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const itemsToRender = selectedUser?.orders?.slice(startIndex, endIndex);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPerPage(pageSize);
  };

  return (
    <div className="user-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>
          {t(`adminUsers:${!isEditMode ? 'create' : 'edit'}`)}
          <div className="exchange-directions-form__tabs">
            <CustomButton
              title={t('user')}
              variant={selectedTab === '1' ? 'filled' : 'outlined'}
              onClick={onClickTab('1')}
            />
            <CustomButton
              title={t('exchanges')}
              variant={selectedTab === '2' ? 'filled' : 'outlined'}
              onClick={onClickTab('2')}
            />
          </div>
        </h3>
        <form onSubmit={onSubmit}>
          <div
            className="user-form__content"
            style={{
              gridTemplateColumns: selectedTab === '1' ? '1fr 1fr' : '1fr',
              maxWidth: selectedTab === '1' ? '696px' : 'unset',
            }}
          >
            {selectedTab === '1' ? (
              <>
                <CustomInput label={t('registerDate')} value={formData?.created_at} disabled />
                <CustomInput label={t('ipAddress')} disabled />
                {/* <CustomInput label={t('Country')} /> */}
                <CustomInput
                  label={t('login')}
                  value={formData?.login}
                  onChange={onChangeInput('login')}
                  errorMessage={formError?.login}
                  required
                />
                <CustomInput
                  type="email"
                  label={t('email')}
                  value={formData?.email}
                  errorMessage={formError?.email}
                  onChange={onChangeInput('email')}
                  required
                />
                <CustomInput
                  label={t('password')}
                  value={formData?.password}
                  errorMessage={formError?.password}
                  onChange={onChangeInput('password')}
                  required={!isEditMode}
                />
                <CustomInput
                  label={t('confirmPassword')}
                  value={formData?.password_confirmation}
                  errorMessage={formError?.password_confirmation}
                  onChange={onChangeInput('password_confirmation')}
                />
                <CustomInput label={t('totalExchanges')} value={formData?.turnover} disabled />
                <CustomInput label={t('2faToken')} disabled />
                <div className="user-form__switch">
                  <CustomSwitch label={t('userVerified')} />
                </div>
              </>
            ) : null}
            {selectedTab === '2' ? (
              <>
                <table className="account-requests__table">
                  <thead>
                    <tr>
                      <th style={{ paddingLeft: '26px' }}>{t('label:id')}</th>
                      <th>{t('label:status')}</th>
                      <th style={{ maxWidth: 200 }}>{t('label:created')}</th>
                      <th style={{ paddingRight: '26px' }}>{t('label:give')}</th>
                      <th>{t('label:receive')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsToRender?.map((item) => {
                      const tdStyles = classNames({
                        'custom-table__row--pending': item.status?.toLowerCase() === 'pending',
                        'custom-table__row--rejected': item.status?.toLowerCase() === 'cancel',
                        'custom-table__row--accepted': ['complete', 'paid'].includes(
                          item.status?.toLowerCase(),
                        ),
                        'custom-table__row--draft': item.status?.toLowerCase() === 'draft',
                      });

                      return (
                        <tr key={item.token}>
                          <td className={tdStyles}>{item?.id ?? '-'}</td>
                          <td className={tdStyles}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Link
                                style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}
                                to={`/exchange/${item?.token}/status`}
                                target="_blank"
                              >
                                <img
                                  src={ProfileInfoIcon}
                                  alt={ProfileInfoIcon}
                                  style={{ marginTop: 0 }}
                                />
                              </Link>
                              {t(`statuses:${item?.status}`)}
                            </div>
                          </td>
                          <td className={tdStyles}>
                            {moment(item.created_at).format('DD.MM.YYYY, HH:mm')}
                          </td>
                          <td className={tdStyles} style={{ paddingRight: '26px' }}>
                            {
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    // whiteSpace: 'break-spaces',
                                  }}
                                >
                                  <img
                                    src={REACT_APP_API_URL + item?.given?.image}
                                    alt={REACT_APP_API_URL + item?.given?.image}
                                    style={{ width: 18, height: 18, marginRight: 10, marginTop: 0 }}
                                  />
                                  {item?.given?.sum}{' '}
                                  <strong style={{ marginLeft: 5 }}>
                                    {item?.given?.name} ({item?.given?.code})
                                  </strong>
                                </div>
                                <div>
                                  {item?.given?.cardNumber ? (
                                    <span>({item?.given?.code})</span>
                                  ) : null}
                                </div>
                              </div>
                            }
                          </td>
                          <td className={tdStyles}>
                            {
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    // whiteSpace: 'break-spaces',
                                  }}
                                >
                                  <img
                                    src={REACT_APP_API_URL + item?.received?.image}
                                    alt={REACT_APP_API_URL + item?.received?.image}
                                    style={{ width: 18, height: 18, marginRight: 10, marginTop: 0 }}
                                  />
                                  {item?.received?.sum}{' '}
                                  <strong style={{ marginLeft: 5 }}>
                                    {item?.received?.name} ({item?.received?.code})
                                  </strong>
                                </div>
                                <div>
                                  {item?.received?.cardNumber ? (
                                    <span>{item?.received?.cardNumber}</span>
                                  ) : null}
                                </div>
                              </div>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <CustomPagination
                  totalItems={selectedUser?.orders?.length}
                  initialPage={currentPage}
                  initialPerPage={perPage}
                  onPageChange={handlePageChange}
                  withSelect={false}
                />
              </>
            ) : null}

            {/* <CustomSelect
              label={t('regAccessToken')}
              options={options}
              onChange={() => {}}
              disabled
            /> */}
            {/* <CustomInput label={t('passAccessToken')} disabled /> */}
            {/* <CustomInput label={t('tokenApi')} disabled /> */}
          </div>
          {selectedTab === '1' ? <CustomButton variant="filled" title={t('title:save')} /> : null}
        </form>
      </WhiteCard>
    </div>
  );
};
